import { Injectable, Injector, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from 'axios';
import { Observable, Observer, from, of, throwError } from 'rxjs';
import { catchError, retry, tap, shareReplay, timeout, finalize } from 'rxjs/operators';
import { Environment, environment } from '../../../environments/environment';
import { LoaderService } from './loader.service';
import { ToasterService } from './toaster.service';
import { AuthService } from './auth.service';
import { CommonService } from './common.service';
import { AccessTokenRefresher } from './access-token-refresher';
import { apiBaseUrlFor } from './prism-new-routing';
import { PARTNER_PROXY_TIMEOUT_MS, joinUrl, partnerProxyKey, partnerProxyUrl } from './partner-proxy';
import { isPrismNewCall, preparePrismNewRequest, toLegacyBody, toLegacyErrorBody } from './prism-new-legacy-shape';
import { isUnpaidProductUnauthorized } from '../../pages/private/unpaid-payments-v2/unpaid-payments-access.util';
import {
  BaseResponse,
  SuccessResponse,
  ErrorResponse,
  PaginatedResponse,
} from '../../../types/api-response';

export interface ApiRequestOptions {
  showLoader?: boolean;
  loaderMessage?: string;
  showSuccessToaster?: boolean;
  successMessage?: string;
  showErrorToaster?: boolean;
  errorMessage?: string;
  cache?: boolean | number;
  addAccessToken?: boolean;
  addSecurityHeaders?: boolean;
  retries?: number;
  timeout?: number;
  headers?: Record<string, string>;
  responseType?: 'json' | 'blob' | 'text' | 'arraybuffer';
  productServerName?: Environment['productServerUrls'][number]['name'];
  withCredentials?: boolean;
  baseUrl?: string;
  /** Keep pagination metadata when the API returns it alongside a top-level data array */
  preservePagination?: boolean;
  isUnpaidPayments?: boolean;
  /**
   * Send the body exactly as given (nulls kept) and emit the whole response body instead of its
   * `data`: for callers written against a microservice's raw `{ success, data }` envelope.
   */
  rawBody?: boolean;
}

interface SecurityContext {
  'x-device-id': string;
}

interface CachedResponse<T = any> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  // Static flag persists across service instances/navigation
  private static refreshTokenFailedFlag = false;
  private static isRedirecting = false;
  private static readonly ACCESS_TOKEN_KEY = 'access_token';

  private axiosInstance!: AxiosInstance;
  private deviceId: string | null = null;
  private responseCache = new Map<string, CachedResponse>();

  private loaderService = inject(LoaderService);
  private toasterService = inject(ToasterService);
  private router = inject(Router);
  private commonService = inject(CommonService);
  private injector = inject(Injector);
  private readonly tokenRefresher = new AccessTokenRefresher({
    requestToken: () => this.requestRefreshedToken(),
    onRefreshed: (token) => this.setAccessToken(token),
    onFailed: () => {
      this.refreshTokenFailed = true;
      this.endSession();
      this.commonService.captureRedirectUrl(this.router.url);
      this.router.navigate(['/login']);
      // Don't show toaster here - let RETRY_LOGIN handler or auth guard handle the message
    },
  });
  private productAxiosInstances = new Map<string, AxiosInstance>();
  /** Partner calls sent through prism-new's partner proxy (prism-new routing on). */
  private partnerProxyAxios = axios.create({
    timeout: PARTNER_PROXY_TIMEOUT_MS,
    withCredentials: environment.api.withCredentials,
    headers: { 'Content-Type': 'application/json' },
  });
  public selectedProductId = signal<string | null>(null);
  /**
   * Numeric `pid` for the currently-selected product.
   *
   * Distinct from `selectedProductId` (which stores the cuid `id` the
   * sidebar dropdown uses for menu/route lookup). The backend's
   * `/dashboards?...&product_id=<n>` endpoint expects this numeric `pid`
   * — set by the sidebar whenever the active product changes (via URL
   * restore, default selection, or dropdown change), so any consumer
   * (e.g. `my-dashboard.component.ts`) can read it without re-resolving.
   */
  public selectedProductPid = signal<string | number | null>(null);
  public products = signal<any[]>([]);

  /**
   * In-flight observable for {@link fetchProducts}. The response cache
   * (`responseCache`) only deduplicates once a request has resolved, so
   * when multiple consumers (sidebar, private-layout, ai-headlines, …)
   * call `fetchProducts()` concurrently at app boot the cache is still
   * empty and every caller hits the network. Sharing a single in-flight
   * `Observable` (refCount + shareReplay) collapses those simultaneous
   * callers onto one HTTP roundtrip.
   */
  private _fetchProductsInFlight: Observable<any[]> | null = null;

  constructor() {
    const storedProductId = sessionStorage.getItem('selected_product_id');
    if (storedProductId) {
      this.selectedProductId.set(storedProductId);
    }

    this.initializeAxios();
    this.initializeDeviceId();
    this.setupInterceptors();
  }

  public setProductAccessToken(
    productName: string,

    token: string,

    isAccessTokenKey: boolean = true
  ): void {
    const productServer = environment.productServerUrls.find(
      (ps) => ps.name === productName
    );

    if (productServer) {
      if (productName === 'credee' && isAccessTokenKey) {
        (productServer as any).accessTokenKey = token;
      } else {
        productServer.authToken = token;
      }

      // Clear existing axios instance to force recreation with new token headers

      this.productAxiosInstances.delete(productName);
    }
  }

  private get refreshTokenFailed(): boolean {
    return ApiService.refreshTokenFailedFlag;
  }

  private set refreshTokenFailed(value: boolean) {
    ApiService.refreshTokenFailedFlag = value;
  }

  private initializeAxios(): void {
    this.axiosInstance = axios.create({
      baseURL: environment.apiUrl,
      timeout: environment.api.timeout,
      withCredentials: environment.api.withCredentials,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add AI bot server if not already present in the map
    if (
      environment.ai &&
      !this.productAxiosInstances.has(environment.ai.productServerName)
    ) {
      this.productAxiosInstances.set(
        environment.ai.productServerName,
        axios.create({
          baseURL: environment.ai.baseUrl,
          withCredentials: environment.ai.withCredentials,
        })
      );
    }

    // Add AI chatbot server if not already present in the map
    if (environment.aiChatBot && !this.productAxiosInstances.has(environment.aiChatBot.productServerName)) {
      this.productAxiosInstances.set(environment.aiChatBot.productServerName, axios.create({
        baseURL: environment.aiChatBot.baseUrl,
        withCredentials: environment.aiChatBot.withCredentials,
      }));
    }

    // Add AI live-call server (deck admin) if not already present in the map
    if ((environment as any).aiLiveCall && !this.productAxiosInstances.has((environment as any).aiLiveCall.productServerName)) {
      this.productAxiosInstances.set(
        (environment as any).aiLiveCall.productServerName,
        axios.create({
          baseURL: (environment as any).aiLiveCall.baseUrl,
          withCredentials: (environment as any).aiLiveCall.withCredentials,
        })
      );
    }
  }

  private initializeDeviceId(): void {
    const stored = localStorage.getItem('device_id');
    if (stored) {
      this.deviceId = stored;
    } else {
      this.deviceId = this.generateDeviceId();
      localStorage.setItem('device_id', this.deviceId);
    }
  }

  private generateDeviceId(): string {
    return `device_${Date.now()}_${Math.random()
      .toString(36)
      .substring(2, 15)}`;
  }

  private setupInterceptors(): void {
    this.axiosInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // Endpoints listed in environment.prismNew go to the prism-new API; a caller-supplied
        // baseUrl (options.baseUrl) is left alone. Every call that ends up on prism-new, listed
        // or pointed there by its caller, is prepared once (a retry is already prepared).
        if (config.baseURL === environment.apiUrl && config.url) {
          config.baseURL = apiBaseUrlFor(config.method ?? 'get', config.url);
        }
        if (config.url && isPrismNewCall(config)) {
          preparePrismNewRequest(config, (id) => {
            if (/^\d+$/.test(String(id))) return Number(id);
            const pid = this.products().find((p) => p?.id === id)?.pid
              ?? (id === this.selectedProductId() ? this.selectedProductPid() : null)
              ?? this.pidFromSettings(id);
            return pid != null && /^\d+$/.test(String(pid)) ? Number(pid) : undefined;
          });
        }
        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        const authHeader =
          response.headers['authorization'] ||
          response.headers['Authorization'];
        if (authHeader && authHeader.startsWith('Bearer ')) {
          const token = authHeader.substring(7);
          this.setAccessToken(token);
        }

        // prism-new bodies are turned back into the old API's shape the app reads.
        if (isPrismNewCall(response.config)) {
          response.data = toLegacyBody(response.config, response.data, response.status);
        }
        return response;
      },
      async (error: AxiosError<ErrorResponse>) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & {
          _retry?: boolean;
        };
        if (error.response && isPrismNewCall(error.config)) {
          error.response.data = toLegacyErrorBody(error.response.data, error.response.status);
        }
        const errorData = error.response?.data;
        const errorAction = errorData?.action;

        if (
          error.response?.status === 401 &&
          errorAction === 'REFRESH_TOKEN' &&
          !originalRequest._retry
        ) {
          if (this.refreshTokenFailed) {
            if (!originalRequest.url?.includes('/auth/authenticate')) {
              this.commonService.captureRedirectUrl(this.router.url);
              this.router.navigate(['/login']);
            }
            return Promise.reject(error);
          }

          if (originalRequest.url?.includes('/auth/refresh')) {
            this.refreshTokenFailed = true;
            this.endSession();
            // Capture current URL before navigation
            const currentUrl = this.router.url;
            this.commonService.captureRedirectUrl(currentUrl);
            // Only show toaster if not on login page or root (which redirects to login)
            if (!currentUrl.includes('/login') && currentUrl !== '/') {
              this.toasterService.error('Session expired. Please log in again.', 'Session Expired');
            }
            this.router.navigate(['/login']);
            return Promise.reject(error);
          }

          originalRequest._retry = true;

          // A request queued behind a refresh that fails stays pending (as before): the login
          // redirect takes over instead of each queued caller surfacing an error.
          const queued = this.tokenRefresher.isRefreshing;
          let newAccessToken: string;
          try {
            newAccessToken = await this.tokenRefresher.refresh();
          } catch (refreshError) {
            return queued ? new Promise(() => {}) : Promise.reject(refreshError);
          }
          if (originalRequest.headers) {
            originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
          }
          return this.axiosInstance(originalRequest);
        }

        if (
          (error.response?.status === 401 || error.response?.status === 403) &&
          (errorAction === 'RETRY_LOGIN' || errorData?.code === 'ROLE_SUSPENDED')
        ) {
          if (ApiService.isRedirecting) {
            return Promise.reject(error);
          }
          ApiService.isRedirecting = true;

          this.refreshTokenFailed = true;
          this.endSession();
          const currentUrl = this.router.url;

          // A suspended role is not something the user logs back into, so only
          // an expired/terminated session is worth returning to.
          if (errorData?.code !== 'ROLE_SUSPENDED') {
            this.commonService.captureRedirectUrl(currentUrl);
          }

          if (!currentUrl.includes('/login') && currentUrl !== '/') {
            if (errorData?.code === 'ROLE_SUSPENDED') {
              this.toasterService.error(errorData.error || 'Your assigned role has been suspended. Please contact support.', 'Role Suspended');
              setTimeout(() => {
                ApiService.isRedirecting = false;
                window.location.href = '/login';
              }, 2000);
              return Promise.reject(error);
            } else {
              this.toasterService.success('Logout successfully.', 'Session Terminated');
            }
          }
          this.router.navigate(['/login']).then(() => {
            ApiService.isRedirecting = false;
          });
        }

        return Promise.reject(error);
      }
    );
  }

  private getSecurityHeaders(): Partial<SecurityContext> {
    const headers: Partial<SecurityContext> = {};

    if (this.deviceId) {
      headers['x-device-id'] = this.deviceId;
    }

    return headers;
  }

  /**
   * Partner auth headers for product-server APIs: authorization, prism-secret-key, email.
   * Values come from session dynamic_routes, storage, and the signed-in user.
   */
  getProductPartnerAuthHeaders(
    productServerName: string,
    isUnpaidPayments: boolean = false
  ): Record<string, string> {
    const runtime = this.getPartnerRuntimeFromSettings(productServerName);
    const headers: Record<string, string> = {};

    // Only the partner's own credentials: the Prism session token must never reach a partner host.
    const accessToken = runtime.access_token || '';
    const secretKey = runtime.secret_key || '';
    const userEmail = runtime.email || this.getPartnerUserEmail() || '';

    // if (productServerName.toLowerCase() === 'credee') {
    //   if (isUnpaidPayments) {
    //     if (accessToken) {
    //       headers['authorization'] = accessToken.startsWith('Bearer ') ? accessToken : `Bearer ${accessToken}`;
    //     }
    //   } else {
    //     const productServer = environment.productServerUrls.find(
    //       (ps) => ps.name === productServerName
    //     );
    //     if (productServer?.accessTokenKey) {
    //       headers['authorization'] = productServer.accessTokenKey;
    //     }
    //   }
    // } else {
    if (accessToken) {
      headers['authorization'] = accessToken;
    }
    // }
    if (secretKey) {
      headers['prism-secret-key'] = secretKey;
    }
    if (userEmail) {
      headers['email'] = userEmail;
    }

    return headers;
  }

  /**
   * Clears every trace of the session, here and in other tabs. AuthService is resolved
   * lazily because it injects this service.
   */
  private endSession(): void {
    try {
      this.injector.get(AuthService).endSession();
    } catch {
      this.clearAccessToken();
    }
  }

  private getPartnerUserEmail(): string {
    try {
      return this.injector.get(AuthService).getUserEmail() || '';
    } catch {
      return '';
    }
  }

  /**
   * A product's numeric pid from get-setting's product groups (sessionStorage `dynamic_routes`), which load
   * before any page runs: covers a new tab or reload whose first calls beat the product list.
   */
  private pidFromSettings(id: unknown): string | number | null {
    try {
      const groups = JSON.parse(sessionStorage.getItem('dynamic_routes') ?? '[]');
      return (Array.isArray(groups) ? groups : []).find((g: any) => g?.product?.id === id)?.product?.pid ?? null;
    } catch {
      return null;
    }
  }

  private getPartnerRuntimeFromSettings(productServerName: string): {
    access_token?: string;
    secret_key?: string;
    email?: string;
    base_url?: string;
  } {
    const raw = sessionStorage.getItem('dynamic_routes');
    if (!raw) {
      return {};
    }

    try {
      const routes = JSON.parse(raw);
      const normalizedServer = (productServerName || '').toLowerCase().trim();
      const norm = (value: string) => value.replace(/-/g, '_');

      const match = (Array.isArray(routes) ? routes : []).find((entry: any) => {
        const product = entry?.product || {};
        const name = String(product?.name || '').toLowerCase().trim();
        const serverName = String(product?.server_name || product?.productServerName || '')
          .toLowerCase()
          .trim();
        const basePathSlug =
          String(product?.basePath || '')
            .toLowerCase()
            .split('/')
            .filter(Boolean)
            .pop() || '';

        const isFmLookup = norm(normalizedServer) === 'finance_mutual';
        if (isFmLookup && (basePathSlug === 'finance_mutual' || name.includes('finance mutual'))) {
          return true;
        }

        return (
          name.includes(normalizedServer) ||
          serverName === normalizedServer ||
          basePathSlug === norm(normalizedServer)
        );
      });

      const source = {
        ...(match?.product || {}),
        ...(match?.settings || {}),
        ...(match || {}),
      };

      return {
        access_token:
          source.access_token ??
          source.accessToken ??
          source.authorization ??
          source.auth_token ??
          source.authToken,
        secret_key:
          source.secret_key ?? source.secretKey ?? source.secret ?? source.secretKeyValue,
        email: source.email ?? source.admin_email ?? source.adminEmail,
        base_url: source.baseUrl ?? source.base_url,
      };
    } catch {
      return {};
    }
  }

  /**
   * Where a partner call goes while prism-new routing is on; null keeps today's direct call.
   * Every partner call is proxied, not only unpaid payments: prism-new's get-setting no longer
   * hands the partner's authorization / secret key to the browser, so a direct call could not
   * authenticate anyway.
   */
  private partnerProxyUrlFor(options: ApiRequestOptions, url: string): string | null {
    const key = partnerProxyKey(options.productServerName);
    const base =
      options.baseUrl ||
      environment.productServerUrls.find((ps) => ps.name === options.productServerName)?.url;
    if (!key || !base) {
      return null;
    }
    const partnerBase = this.getPartnerRuntimeFromSettings(options.productServerName!).base_url;
    return partnerProxyUrl(key, joinUrl(base, url), partnerBase);
  }

  /**
   * Prism session headers plus the caller's own non-credential headers: the proxy attaches the
   * partner's credentials server-side, so none are sent from here.
   */
  private buildPartnerProxyHeaders(options: ApiRequestOptions): Record<string, string> {
    const credentialHeaders = ['authorization', 'prism-secret-key', 'email', 'x-refresh-token'];
    const headers = Object.fromEntries(
      Object.entries(options.headers ?? {}).filter(
        ([key]) => !credentialHeaders.includes(key.toLowerCase())
      )
    );
    const { productServerName: _partner, ...sessionOptions } = options;
    return this.buildHeaders({ ...sessionOptions, addAccessToken: true, headers });
  }

  private applyPartnerAuthHeaders(
    headers: Record<string, string>,
    productServerName: string,
    isUnpaidPayments?: boolean
  ): void {
    const partnerHeaders = this.getProductPartnerAuthHeaders(productServerName, isUnpaidPayments);
    for (const [key, value] of Object.entries(partnerHeaders)) {
      const hasExisting = Object.keys(headers).some(
        (headerKey) => headerKey.toLowerCase() === key.toLowerCase()
      );
      if (!hasExisting && value) {
        headers[key] = value;
      }
    }
  }

  /** POSTs /auth/refresh; resolves the issued access token, or null when none came back. */
  private async requestRefreshedToken(): Promise<string | null> {
    const csrfToken = this.getCsrfToken();
    const headers: Record<string, string> = {
      'x-device-id': this.deviceId || '',
    };
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }

    const response = await this.axiosInstance.post<
      SuccessResponse<{ access_token?: string }>
    >('/auth/refresh', {}, { headers });

    const authHeader =
      response.headers['authorization'] || response.headers['Authorization'];
    if (authHeader && authHeader.startsWith('Bearer ')) {
      return authHeader.substring(7);
    }
    return response.data?.data?.access_token || null;
  }

  private getCsrfToken(): string | null {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'prism-auth_csrf_token') {
        return decodeURIComponent(value);
      }
    }
    return null;
  }

  private buildHeaders(options: ApiRequestOptions): Record<string, string> {
    if (options.productServerName) {
      let headers: Record<string, string> = {};

      if (options.headers) {
        Object.assign(headers, options.headers);
      }

      // Every partner call authenticates as the partner (authorization / prism-secret-key / email from
      // get-setting): the report tools (daily cron, monthly reports, arrangements, ...) reject a call
      // without the secret key, not only unpaid payments. Calls sent through prism-new's partner proxy
      // don't come here (buildPartnerProxyHeaders), so their credentials still stay server-side.
      this.applyPartnerAuthHeaders(headers, options.productServerName, options.isUnpaidPayments);

      const productServer = environment.productServerUrls.find(
        (ps) => ps.name === options.productServerName
      );

      if (!productServer) {
        // console.warn(
        //   `Product server configuration not found for: ${options.productServerName}`
        // );
        return headers;
      }

      if (['credee', 'credee-live'].includes(productServer.name)) {
        const token = productServer.accessTokenKey;
        const refreshToken = productServer.xRefreshToken;
        let customAuth = headers['Authorization'] || headers['authorization'];
        if (customAuth) {
          if (!customAuth.startsWith('Bearer ')) {
            customAuth = `Bearer ${customAuth}`;
          }
          headers['Authorization'] = customAuth;
          if (headers['authorization']) {
            delete headers['authorization'];
          }
        } else if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
        if (refreshToken && !headers['x-refresh-token']) {
          headers['x-refresh-token'] = refreshToken;
        }
      }

      if (
        productServer.useAuth &&
        productServer.authToken &&
        productServer.authKeyname
      ) {
        if (productServer.includeIn === 'header') {
          const authKey = productServer.authKeyname;
          const authKeyLower = authKey.toLowerCase();
          const hasExistingAuth = Object.keys(headers).some(
            (k) => k.toLowerCase() === authKeyLower
          );
          if (!hasExistingAuth) {
            let tokenVal = productServer.authToken;
            if (!tokenVal.startsWith('Bearer ')) {
              tokenVal = `Bearer ${tokenVal}`;
            }
            headers[authKey] = tokenVal;
          }

          return headers;
        }
      }

      // Return empty object for product server without auth or body auth
      return headers;
    }

    const headers: Record<string, string> = {};

    const token = this.getAccessToken();
    if (options.addAccessToken !== false && token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const csrfToken = this.getCsrfToken();
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }

    if (options.addSecurityHeaders !== false) {
      const securityHeaders = this.getSecurityHeaders();
      Object.assign(headers, securityHeaders);
    }

    if (options.headers) {
      Object.assign(headers, options.headers);
    }

    return headers;
  }

  private buildBody(options: ApiRequestOptions, data: any): any {
    if (options.productServerName) {
      const productServer = environment.productServerUrls.find(
        (ps) => ps.name === options.productServerName
      );

      if (!productServer) {
        // console.warn(
        //   `Product server configuration not found for: ${options.productServerName}`
        // );
        return data;
      }

      // Frontend-only properties that should never be sent to the backend
      const frontendOnlyProps = [
        'showErrorToaster',
        'showSuccessToaster',
        'showLoader',
        'loaderMessage',
        'cache',
        'addAccessToken',
        'addSecurityHeaders',
        'productServerName',
        'retries',
        'timeout',
        'responseType',
        'withCredentials',
        'successMessage',
        'errorMessage',
        'headers',
        'baseUrl',
      ];

      // Ensure data is an object for product server requests
      let bodyData: any;
      if (data !== undefined && data !== null) {
        if (typeof data === 'object' && !Array.isArray(data)) {
          // Filter out frontend-only properties
          bodyData = Object.entries(data).reduce((obj, [key, value]) => {
            if (!frontendOnlyProps.includes(key)) {
              obj[key] = value;
            }
            return obj;
          }, {} as any);
        } else {
          // For non-object data, create a new object
          bodyData = { data };
        }
      } else {
        // If data is null or undefined, start with empty object
        bodyData = {};
      }

      // Add productId if available and not already present
      // Check both signal and sessionStorage as fallback
      const productId =
        this.getSelectedProductId() ||
        sessionStorage.getItem('selected_product_id');
      if (productId && !bodyData.productId) {
        // Ensure productId is a string
        bodyData.productId = String(productId);
      }

      // Add auth token if configured for body
      if (
        productServer.useAuth &&
        productServer.authToken &&
        productServer.authKeyname &&
        productServer.includeIn === 'body'
      ) {
        bodyData[productServer.authKeyname] = productServer.authToken;
      }

      return bodyData;
    }

    return data;
  }

  private buildCacheKey(
    method: string,
    url: string,
    params?: any,
    data?: any
  ): string {
    const paramsStr = params ? JSON.stringify(params) : '';
    const dataStr = data ? JSON.stringify(data) : '';
    return `${method}:${url}:${paramsStr}:${dataStr}`;
  }

  private getCachedResponse<T>(cacheKey: string): T | null {
    const cached = this.responseCache.get(cacheKey);
    if (!cached) return null;

    const now = Date.now();
    if (now > cached.expiresAt) {
      this.responseCache.delete(cacheKey);
      return null;
    }
    return cached.data as T;
  }

  private cacheResponse<T>(
    cacheKey: string,
    data: T,
    cacheDuration: number
  ): void {
    const now = Date.now();
    this.responseCache.set(cacheKey, {
      data,
      timestamp: now,
      expiresAt: now + cacheDuration,
    });
  }

  private request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    url: string,
    options: ApiRequestOptions = {},
    data?: any,
    params?: any
  ): Observable<T> {
    params = params === null ? undefined : params;
    // Strip null/undefined fields from plain-object bodies. FormData must pass through
    // untouched: Object.entries() would turn it into {} and break multipart uploads
    // (axios detects FormData and lets the browser set the multipart boundary).
    data = data && !(data instanceof FormData) && !options.rawBody
      ? Object.entries(data)
        .filter(([, value]) => value !== undefined && value !== null)
        .reduce((obj, [key, value]) => {
          obj[key] = value;
          return obj;
        }, {} as any)
      : data;

    if (options.productServerName) {
      return this.productRequest(method, url, options, data, params);
    }

    return new Observable((observer: Observer<T>) => {
      const controller = new AbortController();
      if (options.showLoader) {
        this.loaderService.show({
          message: options.loaderMessage || 'Loading...',
        });
      }

      if (method === 'GET' && options.cache) {
        const cacheKey = this.buildCacheKey(method, url, params, data);
        const cachedData = this.getCachedResponse<T>(cacheKey);
        if (cachedData) {
          observer.next(cachedData);
          observer.complete();
          if (options.showLoader) {
            this.loaderService.hide();
          }
          return;
        }
      }

      const axiosInstance = this.axiosInstance;

      const headers = this.buildHeaders(options);
      const body = this.buildBody(options, data);

      // The instance default Content-Type is application/json, and axios v1 SERIALIZES
      // FormData to JSON when the content-type says JSON (File entries become {} and the
      // backend rejects the body). Ask for multipart here; the browser adapter replaces
      // it with the real boundary version at send time.
      if (body instanceof FormData) {
        headers['Content-Type'] = 'multipart/form-data';
      }

      const config: AxiosRequestConfig = {
        method,
        url,
        headers,
        data: body,
        params,
        signal: controller.signal,
        timeout: options.timeout || environment.api.timeout,
        responseType: options.responseType || 'json',
        withCredentials: options.withCredentials !== undefined ? options.withCredentials : environment.api.withCredentials,
        ...(options.baseUrl ? { baseURL: options.baseUrl } : {}),
      };

      axiosInstance
        .request<SuccessResponse<T> | T>(config)
        .then((response: AxiosResponse<SuccessResponse<T> | T>) => {
          let responseData: T;

          const rawData = response.data as any;
          if (options.rawBody) {
            responseData = rawData as T;
          } else if (rawData && typeof rawData === 'object' && 'success' in rawData) {
            const baseResponse = rawData as SuccessResponse<T> & {
              pagination?: PaginatedResponse['pagination'];
            };

            if (options.preservePagination && rawData.pagination) {
              responseData = {
                data: baseResponse.data,
                pagination: rawData.pagination,
              } as T;
            } else {
              responseData = baseResponse.data as T;
            }

            if (
              options.showSuccessToaster &&
              (baseResponse.message || options.successMessage)
            ) {
              this.toasterService.success(
                options.successMessage ||
                baseResponse.message ||
                'Request Successful'
              );
            }
          } else {
            responseData = response.data as T;

            if (options.showSuccessToaster) {
              this.toasterService.success(
                options.successMessage || 'Operation completed successfully'
              );
            }
          }

          if (method === 'GET' && options.cache) {
            const cacheKey = this.buildCacheKey(method, url, params, data);
            const cacheDuration =
              typeof options.cache === 'number'
                ? options.cache
                : environment.api.cacheTTL;
            this.cacheResponse(cacheKey, responseData, cacheDuration);
          }

          if (options.showLoader) {
            this.loaderService.hide();
          }

          observer.next(responseData);
          observer.complete();
        })
        .catch((error: AxiosError<ErrorResponse>) => {
          if (axios.isCancel(error)) {
            return;
          }

          const errorData = error.response?.data as any;
          const formattedMessage = this.formatErrorMessage(errorData, error.message || 'An error occurred');

          if (options.showErrorToaster !== false) {
            const errorData = error.response?.data as ErrorResponse;
            const errorMessage =
              options.errorMessage ||
              errorData?.error ||
              errorData?.message ||
              error.message ||
              'An error occurred';
            this.toasterService.error(errorMessage);
          }

          if (options.showLoader) {
            this.loaderService.hide();
          }

          observer.error(error.response?.data || error);
        });

      return () => controller.abort();
    }).pipe(
      retry({
        count: options.retries ?? environment.api.retries,
        delay: (error, retryCount) => {
          const delayMs =
            environment.api.retryDelay * Math.pow(2, retryCount - 1);
          // console.warn(
          //   `Retrying request (${retryCount}/${options.retries ?? environment.api.retries
          //   }) after ${delayMs}ms`
          // );
          return new Observable<void>((obs) => {
            setTimeout(() => {
              obs.next(undefined);
              obs.complete();
            }, delayMs);
          });
        },
      }),
      timeout(options.timeout || environment.api.timeout),
      catchError((error: any) => {
        // console.error('API request failed:', error);
        return throwError(() => error);
      })
    );
  }

  private productRequest(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    url: string,
    options: ApiRequestOptions = {},
    data?: any,
    params?: any
  ): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      // Frontend-only properties that should never be sent to the backend
      const frontendOnlyProps = [
        'showErrorToaster',
        'showSuccessToaster',
        'showLoader',
        'loaderMessage',
        'cache',
        'addAccessToken',
        'addSecurityHeaders',
        'productServerName',
        'retries',
        'timeout',
        'responseType',
        'withCredentials',
        'successMessage',
        'errorMessage',
        'headers',
        'baseUrl',
        'isUnpaidPayments',
      ];

      // Clean data: remove undefined/null values AND frontend-only properties
      const controller = new AbortController();
      data = data
        ? Object.entries(data)
          .filter(([key, value]) =>
            value !== undefined &&
            value !== null &&
            !frontendOnlyProps.includes(key)
          )
          .reduce((obj, [key, value]) => {
            obj[key] = value;
            return obj;
          }, {} as any)
        : data;

      if (options.showLoader) {
        this.loaderService.show({
          message: options.loaderMessage || 'Loading...',
        });
      }

      let productAxios: AxiosInstance | undefined;
      const proxyUrl = this.partnerProxyUrlFor(options, url);

      if (proxyUrl) {
        productAxios = this.partnerProxyAxios;
      } else if (options.baseUrl) {
        productAxios = axios.create({
          baseURL: options.baseUrl,
          timeout: environment.api.timeout,
          withCredentials:
            options.withCredentials !== undefined
              ? options.withCredentials
              : false,
          headers: {
            'Content-Type': 'application/json',
          },
        });
      } else {
        productAxios = this.productAxiosInstances.get(
          options.productServerName!
        );

        if (!productAxios) {
          const productServer = environment.productServerUrls.find(
            (ps) => ps.name === options.productServerName
          );

          if (!productServer) {
            observer.error(
              new Error(
                `Product server configuration not found for: ${options.productServerName}`
              )
            );
            if (options.showLoader) {
              this.loaderService.hide();
            }
            return;
          }

          productAxios = axios.create({
            baseURL: productServer.url,
            timeout: environment.api.timeout,
            withCredentials: productServer.withCredentials ?? false,
            headers: {
              'Content-Type': 'application/json',
            },
          });

          this.productAxiosInstances.set(
            options.productServerName!,
            productAxios
          );
        }
      }

      const headers = proxyUrl ? this.buildPartnerProxyHeaders(options) : this.buildHeaders(options);
      const body = this.buildBody(options, data);

      const config: AxiosRequestConfig = {
        method,
        url: proxyUrl ?? url,
        headers,
        data: body,
        params,
        signal: controller.signal,
        timeout: options.timeout || (proxyUrl ? PARTNER_PROXY_TIMEOUT_MS : environment.api.timeout),
        responseType: options.responseType || 'json',
      };

      productAxios
        .request(config)
        .then((response: AxiosResponse<any>) => {
          // Handle success - look for msg, message in response
          const responseData = response.data;

          if (isUnpaidProductUnauthorized(responseData)) {
            if (options.showLoader) {
              this.loaderService.hide();
            }
            observer.next(responseData);
            observer.complete();
            return;
          }

          if (
            responseData.hasOwnProperty('is_error') &&
            responseData.is_error == 1
          ) {
            const message =
              responseData?.message ||
              responseData?.msg ||
              responseData?.err ||
              responseData?.error ||
              'An error occurred';
            throw new Error(message);
          }

          if (options.showSuccessToaster) {
            const successMessage =
              options.successMessage ||
              responseData?.msg ||
              responseData?.message ||
              'Operation completed successfully';

            this.toasterService.success(successMessage);
          }

          if (options.showLoader) {
            this.loaderService.hide();
          }

          observer.next(responseData);
          observer.complete();
        })
        .catch((error: AxiosError<any>) => {
          if (axios.isCancel(error)) {
            return;
          }

          // Handle error - look for err, error, message in error.response.data
          const errorBody = error.response?.data;
          if (isUnpaidProductUnauthorized(errorBody)) {
            if (options.showLoader) {
              this.loaderService.hide();
            }
            observer.next(errorBody);
            observer.complete();
            return;
          }

          if (options.showErrorToaster !== false) {
            const errorData = error.response?.data;
            const errorMessage =
              options.errorMessage ||
              errorData?.err ||
              errorData?.error ||
              errorData?.message ||
              errorData?.msg ||
              error.message ||
              'An error occurred';

            this.toasterService.error(errorMessage);
          }

          if (options.showLoader) {
            this.loaderService.hide();
          }

          // Return error.response.data like in the original request method
          observer.error(error.response?.data || error);
        });

      return () => controller.abort();
    });
  }

  fetchProducts(showLoader: boolean = false): Observable<any[]> {
    if (this.products().length > 0) {
      return of(this.products());
    }

    if (this._fetchProductsInFlight) {
      return this._fetchProductsInFlight;
    }

    this._fetchProductsInFlight = this.get<any[]>(
      '/product/list-product',
      {},
      { cache: true, showLoader: showLoader },
    ).pipe(
      tap((res) => {
        if (Array.isArray(res)) {
          this.products.set(res);
        }
      }),
      finalize(() => {
        this._fetchProductsInFlight = null;
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    return this._fetchProductsInFlight;
  }

  get<T>(
    url: string,
    params?: any,
    options: ApiRequestOptions = {}
  ): Observable<T> {
    return this.request<T>('GET', url, options, undefined, params);
  }

  post<T>(
    url: string,
    data?: any,
    options: ApiRequestOptions = {}
  ): Observable<T> {
    return this.request<T>('POST', url, options, data);
  }

  put<T>(
    url: string,
    data?: any,
    options: ApiRequestOptions = {}
  ): Observable<T> {
    return this.request<T>('PUT', url, options, data);
  }

  delete<T>(url: string, options: ApiRequestOptions = {}): Observable<T> {
    return this.request<T>('DELETE', url, options);
  }

  patch<T>(
    url: string,
    data?: any,
    options: ApiRequestOptions = {}
  ): Observable<T> {
    return this.request<T>('PATCH', url, options, data);
  }

  // Product API convenience methods
  productGet(
    url: string,
    productServerName: string,
    params?: any,
    options: Partial<ApiRequestOptions> = {}
  ): Observable<any> {
    return this.productRequest(
      'GET',
      url,
      { ...options, productServerName },
      undefined,
      params
    );
  }

  productPost(
    url: string,
    productServerName: string,
    data?: any,
    options: Partial<ApiRequestOptions> = {}
  ): Observable<any> {
    return this.productRequest(
      'POST',
      url,
      { ...options, productServerName },
      data
    );
  }

  productPut(
    url: string,
    productServerName: string,
    data?: any,
    options: Partial<ApiRequestOptions> = {}
  ): Observable<any> {
    return this.productRequest(
      'PUT',
      url,
      { ...options, productServerName },
      data
    );
  }

  productDelete(
    url: string,
    productServerName: string,
    options: Partial<ApiRequestOptions> = {}
  ): Observable<any> {
    return this.productRequest('DELETE', url, {
      ...options,
      productServerName,
    });
  }

  productPatch(
    url: string,
    productServerName: string,
    data?: any,
    options: Partial<ApiRequestOptions> = {}
  ): Observable<any> {
    return this.productRequest(
      'PATCH',
      url,
      { ...options, productServerName },
      data
    );
  }

  setAccessToken(token: string | null): void {
    if (token) {
      sessionStorage.setItem(ApiService.ACCESS_TOKEN_KEY, token);
      this.refreshTokenFailed = false;
    } else {
      sessionStorage.removeItem(ApiService.ACCESS_TOKEN_KEY);
    }
  }

  getAccessToken(): string | null {
    return sessionStorage.getItem(ApiService.ACCESS_TOKEN_KEY);
  }

  /**
   * Refreshes the session's access token through the same single-flight refresh the 401
   * interceptor uses (joining one already in flight). Resolves the new token, or null when the
   * session cannot be refreshed; a failed refresh ends the session exactly as a 401 would.
   */
  refreshAccessToken(): Promise<string | null> {
    if (this.refreshTokenFailed) {
      return Promise.resolve(null);
    }
    return this.tokenRefresher.refresh().catch(() => null);
  }

  clearAccessToken(): void {
    sessionStorage.removeItem(ApiService.ACCESS_TOKEN_KEY);
  }

  clearAllCookies(): void {
    const cookies = document.cookie.split(';');
    const cookieNames: string[] = [];

    for (const cookie of cookies) {
      const [name] = cookie.trim().split('=');

      if (name) {
        cookieNames.push(name);

        const expireDate = 'Thu, 01 Jan 1970 00:00:00 UTC';
        const domain = window.location.hostname;
        const domainParts = domain.split('.');

        document.cookie = `${name}=; expires=${expireDate}; path=/`;

        document.cookie = `${name}=; expires=${expireDate}; path=/; domain=${domain}`;

        document.cookie = `${name}=; expires=${expireDate}; path=/; domain=.${domain}`;

        if (domainParts.length > 2) {
          const parentDomain = domainParts.slice(-2).join('.');
          document.cookie = `${name}=; expires=${expireDate}; path=/; domain=${parentDomain}`;
          document.cookie = `${name}=; expires=${expireDate}; path=/; domain=.${parentDomain}`;
        }

        if (domainParts.length >= 2) {
          const rootDomain = '.' + domainParts.slice(-2).join('.');
          document.cookie = `${name}=; expires=${expireDate}; path=/; domain=${rootDomain}`;
        }

        document.cookie = `${name}=; expires=${expireDate}`;

        document.cookie = `${name}=; expires=${expireDate}; path=`;
      }
    }

    const remainingCookies = document.cookie
      .split(';')
      .filter((c) => c.trim()).length;

    if (remainingCookies > 0) {
      // console.warn(
      //   ` ${remainingCookies} cookie(s) could not be cleared by JavaScript. ` +
      //   `These are likely HttpOnly cookies that can only be cleared by the server. ` +
      //   `Attempted to clear: ${cookieNames.join(', ')}`
      // );
    } else {
    }
  }

  setDeviceId(deviceId: string): void {
    this.deviceId = deviceId;
    localStorage.setItem('device_id', deviceId);
  }

  getDeviceId(): string | null {
    return this.deviceId;
  }

  clearCache(url?: string): void {
    if (url) {
      const keysToDelete: string[] = [];
      this.responseCache.forEach((value, key) => {
        if (key.includes(url)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach((key) => this.responseCache.delete(key));
      // console.debug(`Cleared cache for URL: ${url}`);
    } else {
      this.responseCache.clear();
      // console.debug('Cleared all response cache');
    }
  }

  isCached(url: string): boolean {
    let found = false;
    this.responseCache.forEach((value, key) => {
      if (key.includes(url) && Date.now() <= value.expiresAt) {
        found = true;
      }
    });
    return found;
  }

  getCacheStats(): { total: number; expired: number; active: number } {
    const now = Date.now();
    let expired = 0;
    let active = 0;

    this.responseCache.forEach((value) => {
      if (now > value.expiresAt) {
        expired++;
      } else {
        active++;
      }
    });

    return {
      total: this.responseCache.size,
      expired,
      active,
    };
  }

  // Helper methods for product server APIs

  /**
   * Get available product server configurations
   */
  getAvailableProductServers(): Array<{ name: string; url: string }> {
    return environment.productServerUrls.map((server) => ({
      name: server.name,
      url: server.url,
    }));
  }

  /**
   * Check if a product server is configured
   */
  isProductServerConfigured(serverName: string): boolean {
    return environment.productServerUrls.some(
      (server) => server.name === serverName
    );
  }

  /**
   * Make a GET request to a specific product server
   */
  productServerGet<T>(
    productServerName: Environment['productServerUrls'][number]['name'],
    url: string,
    params?: any,
    options: Omit<ApiRequestOptions, 'productServerName'> = {}
  ): Observable<T> {
    return this.get<T>(url, params, { ...options, productServerName });
  }

  /**
   * Make a POST request to a specific product server
   */
  productServerPost<T>(
    productServerName: Environment['productServerUrls'][number]['name'],
    url: string,
    data?: any,
    options: Omit<ApiRequestOptions, 'productServerName'> = {}
  ): Observable<T> {
    return this.post<T>(url, data, { ...options, productServerName });
  }

  /**
   * Make a PUT request to a specific product server
   */
  productServerPut<T>(
    productServerName: Environment['productServerUrls'][number]['name'],
    url: string,
    data?: any,
    options: Omit<ApiRequestOptions, 'productServerName'> = {}
  ): Observable<T> {
    return this.put<T>(url, data, { ...options, productServerName });
  }

  /**
   * Make a DELETE request to a specific product server
   */
  productServerDelete<T>(
    productServerName: Environment['productServerUrls'][number]['name'],
    url: string,
    options: Omit<ApiRequestOptions, 'productServerName'> = {}
  ): Observable<T> {
    return this.delete<T>(url, { ...options, productServerName });
  }

  /**
   * Make a PATCH request to a specific product server
   */
  productServerPatch<T>(
    productServerName: Environment['productServerUrls'][number]['name'],
    url: string,
    data?: any,
    options: Omit<ApiRequestOptions, 'productServerName'> = {}
  ): Observable<T> {
    return this.patch<T>(url, data, { ...options, productServerName });
  }

  /**
   * Clear product server axios instances (useful for testing or configuration changes)
   */
  clearProductServerInstances(): void {
    this.productAxiosInstances.clear();
  }

  setSelectedProductId(productId: string | null): void {

    this.selectedProductId.set(productId);
    if (productId) {
      sessionStorage.setItem('selected_product_id', productId);
    } else {
      sessionStorage.removeItem('selected_product_id');
    }
  }

  getSelectedProductId(): string | null {
    return this.selectedProductId();
  }

  clearSelectedProductId(): void {
    this.selectedProductId.set(null);
  }

  /**
   * Mirror setter for {@link selectedProductPid}. Also persists to
   * sessionStorage under `selected_product_pid` so a hard refresh can
   * rehydrate the value before the sidebar finishes resolving products.
   */
  setSelectedProductPid(pid: string | number | null): void {
    this.selectedProductPid.set(pid);
    if (pid != null) {
      sessionStorage.setItem('selected_product_pid', String(pid));
    } else {
      sessionStorage.removeItem('selected_product_pid');
    }
  }

  getSelectedProductPid(): string | number | null {
    return this.selectedProductPid();
  }

  clearSelectedProductPid(): void {
    this.selectedProductPid.set(null);
    sessionStorage.removeItem('selected_product_pid');
  }

  private formatErrorMessage(data: any, defaultMsg: string): string {
    if (!data) return defaultMsg;

    // NestJS often puts validation errors in 'message' as an array
    // We prioritize 'message' if it's an array, otherwise try other fields
    let message = data.message || data.msg || data.error || data.err;

    if (Array.isArray(message)) {
      return message.join('\n');
    }

    return typeof message === 'string' ? message : defaultMsg;
  }
}
